/**
 * MCP connect handoff (ENG-2209).
 *
 * `account-mcp` (the customer-facing MCP server) does not run its own login page — it sends the
 * customer here, to the domain they already trust, and we hand their Firebase tokens back to it.
 *
 * ⚠️ THE CALLBACK ALLOWLIST IS THE SECURITY BOUNDARY OF THIS FLOW.
 *
 * This page POSTs the signed-in customer's Firebase **refresh token** to `callback`. Anyone who
 * receives that token can act as the customer until it is revoked. The `callback` value arrives in
 * the URL, so a phishing link could otherwise point it at an attacker's server and harvest tokens
 * from any customer who clicked. Only exact, known origins are permitted — never a wildcard, never a
 * *.run.app pattern (anyone can deploy a service on run.app and name it whatever they like).
 *
 * Cloud Run URLs are deterministic (`https://<service>-<project-number>.<region>.run.app`), so the
 * real origins are known ahead of the first deploy and hardcoded below.
 *
 * Each service is reachable at BOTH its custom domain and its run.app URL, and both are listed. The
 * custom domain is what `SERVER_URL` advertises (and therefore what account-mcp sends as `callback`);
 * the run.app origins stay listed so a service still works if its domain mapping is removed or its
 * managed certificate lapses. Both are origins we control, which is the only bar that matters here.
 */

import { INITIAL_HASH } from './pageRouting';

const PROJECT_NUMBER = '678457575280';
const REGION = 'us-central1';

const DEFAULT_ALLOWED_MCP_ORIGINS = [
  'https://account-mcp.starthaven.com',
  'https://account-mcp-alpha.starthaven.com',
  'https://account-mcp-dev.starthaven.com',
  `https://account-mcp-${PROJECT_NUMBER}.${REGION}.run.app`,
  `https://account-mcp-alpha-${PROJECT_NUMBER}.${REGION}.run.app`,
  // The shared per-MR dev service. Listing it lets a branch deploy be exercised end-to-end without
  // a special build. It is safe to allow: it is OUR service, so a link that aims the handoff at it
  // hands the tokens to us, not to an attacker — and account-mcp itself only redirects auth codes
  // to allow-listed MCP clients. What must never be allow-listed is an origin we don't control.
  `https://account-mcp-dev-${PROJECT_NUMBER}.${REGION}.run.app`,
  'http://localhost:10834',
];

/** Optional build-time override (comma-separated), e.g. for a per-branch dev MCP. */
const CONFIGURED = (process.env.REACT_APP_MCP_CALLBACK_ORIGINS ?? '')
  .split(',')
  .map((s) => s.trim())
  .filter(Boolean);

export const ALLOWED_MCP_ORIGINS = CONFIGURED.length > 0 ? CONFIGURED : DEFAULT_ALLOWED_MCP_ORIGINS;

export interface McpAuthRequest {
  /** Signed, opaque state minted by account-mcp; we hand it back untouched. */
  handoff: string;
  /** Where to POST the tokens. Already validated against {@link ALLOWED_MCP_ORIGINS}. */
  callback: string;
}

/**
 * Parse `#/mcp-auth?handoff=…&callback=…` out of the URL hash, returning null when this isn't an MCP
 * connect request OR when the callback is not an allow-listed MCP origin.
 *
 * A rejected callback returns null (the app falls through to its normal routing) rather than
 * rendering the consent page — we never show a "connect" prompt for a destination we won't send to.
 *
 * ⚠️ Defaults to {@link INITIAL_HASH}, NOT `window.location.hash` — same reason as the directory
 * callback (ENG-2586). App's hash-sync effect rewrites the hash on its FIRST commit, and
 * `/mcp-auth` is not a PageId so it normalizes to `home`, i.e. the empty hash. That commit lands
 * before Firebase auth has hydrated from IndexedDB, so `authHook.userInfo` is still null and App's
 * consent-screen branch has not fired yet. Reading live, this function then found an already-empty
 * hash and returned null forever: the customer landed on the dashboard and the handoff was gone.
 * Capturing at import time is what makes the request survive that first commit.
 */
export function parseMcpAuthRequest(rawHash: string = INITIAL_HASH): McpAuthRequest | null {
  const hash = rawHash.replace(/^#/, '');
  const [path, query] = hash.split('?');
  if (path !== '/mcp-auth' && path !== 'mcp-auth') return null;

  const params = new URLSearchParams(query ?? '');
  const handoff = params.get('handoff');
  const callback = params.get('callback');
  if (!handoff || !callback) return null;

  if (!isAllowedCallback(callback)) {
    // Loud, because the only ways to get here are a misconfiguration or an attempt to harvest a
    // customer's Firebase tokens.
    console.error('[mcp-auth] Refusing handoff: callback origin is not allow-listed:', callback);
    return null;
  }

  return { handoff, callback };
}

export function isAllowedCallback(callback: string): boolean {
  let url: URL;
  try {
    url = new URL(callback);
  } catch {
    return false;
  }
  // Compare on ORIGIN, and require the path we expect — not a prefix match, which `startsWith`
  // would let an attacker defeat with e.g. https://account-mcp-…run.app.evil.com.
  return ALLOWED_MCP_ORIGINS.includes(url.origin) && url.pathname === '/oauth/handoff';
}

/**
 * account-mcp's deny endpoint (ENG-3490), derived from an already-validated `callback`.
 *
 * `callback` is always exactly `<origin>/oauth/handoff` — checked by {@link isAllowedCallback} before
 * this page ever renders. This never takes a path from the request: `/oauth/deny` is a fixed literal
 * appended to that same validated origin, so it carries no new open-redirect surface.
 */
export function denyEndpointFor(callback: string): string {
  return `${new URL(callback).origin}/oauth/deny`;
}
